import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import { STATUS_LABELS, STATUS_COLORS } from '../../constants/statuses';
import { format } from 'date-fns';

const TimelineEvent = ({
    reservation,
    pxPerMin,
    visibleStart,
    onClick,
    onContextMenu,
    laneIndex = 0,
    totalLanes = 1,
    isMasked = false,
    canDrag = true
}) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: reservation.id,
        data: { type: 'reservation', reservation },
        disabled: isMasked || !canDrag
    });

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const hoverTimeout = useRef(null);

    const handleMouseEnter = (e) => {
        if (isDragging) return;

        // Calculate position based on viewport to avoid clipping issues in scrollable containers
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPos({
            top: rect.top + (rect.height / 2),
            left: rect.right
        });

        // Clear any existing timeout to prevent race conditions
        if (hoverTimeout.current) clearTimeout(hoverTimeout.current);

        hoverTimeout.current = setTimeout(() => {
            setShowTooltip(true);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
            hoverTimeout.current = null;
        }
        setShowTooltip(false);
    };

    // Clean up tooltip if component unmounts while tooltip is showing
    useEffect(() => {
        return () => setShowTooltip(false);
    }, []);

    const style = {
        transform: CSS.Translate.toString(transform),
        left: `${(new Date(reservation.startTime) - visibleStart) / 60000 * pxPerMin}px`,
        width: `${(new Date(reservation.endTime) - new Date(reservation.startTime)) / 60000 * pxPerMin}px`,
        backgroundColor: isMasked ? '#475569' : (reservation.statusConfig?.color || '#555'),
        color: isMasked ? 'rgba(255,255,255,0.6)' : (reservation.statusConfig?.textColor || '#fff'),
        border: isMasked ? '1px dashed rgba(255,255,255,0.1)' : 'none',
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : showTooltip ? 100 : 5,
        height: `${100 / totalLanes}%`, // Share height
        top: `${(100 / totalLanes) * laneIndex}%`, // Stack vertically
        position: 'absolute' // Ensure absolute positioning within lane
    };

    // Calculate duration for display
    const rawDiff = (new Date(reservation.endTime) - new Date(reservation.startTime)) / 60000;
    const displayDuration = Math.round(rawDiff);
    const isCompact = totalLanes > 1;
    const timeRange = `${dayjs(reservation.startTime).format('HH:mm')} - ${dayjs(reservation.endTime).format('HH:mm')}`;
    const statusLabel = STATUS_LABELS[reservation.status] || reservation.status || '-';
    const statusColor = STATUS_COLORS[reservation.status] || '#3b82f6';

    return (
        <>
            <div
                ref={setNodeRef}
                style={style}
                {...(canDrag ? listeners : {})}
                {...(canDrag ? attributes : {})}
                className={`timeline-event ${!canDrag ? 'cursor-default' : ''}`}
                onClick={(e) => onClick && onClick(e, reservation)}
                onContextMenu={(e) => onContextMenu && onContextMenu(e, reservation)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="timeline-event-inner" style={{ padding: isCompact ? '2px 4px' : '6px 8px', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>
                    {/* Note Indicator */}
                    {reservation.note && (
                        <div style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            fontSize: '10px',
                            background: '#fbbf24',
                            color: '#000',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                            zIndex: 10
                        }}>
                            💬
                        </div>
                    )}
                    {isCompact ? (
                        <div className="font-bold leading-none truncate" style={{ fontSize: '10px' }}>
                            {isMasked ? 'DOLU' : (reservation.reservationNumber || reservation.documentNumber || reservation.plateNumber || '-')}
                        </div>
                    ) : (
                        <>
                            {(reservation.reservationNumber || reservation.documentNumber) && !isMasked && (
                                <div className="font-bold leading-none truncate mb-1" style={{ fontSize: '11px' }}>
                                    {reservation.reservationNumber || reservation.documentNumber}
                                </div>
                            )}
                            <div className="font-semibold leading-none truncate mb-1" style={{ fontSize: '10px' }}>
                                {isMasked ? 'DOLU' : (reservation.plateNumber || '-')}
                                {!isMasked && reservation.driverName && (
                                    <span style={{ marginLeft: '4px', opacity: 0.7, fontWeight: 400 }}>| {reservation.driverName}</span>
                                )}
                            </div>
                            <div className="opacity-90 truncate leading-none" style={{ fontSize: '9px' }}>
                                {isMasked ? 'GİZLİ' : (reservation.transporterShortName || reservation.transporterName || '-')}
                            </div>
                            {displayDuration > 30 && (
                                <div className="opacity-75 mt-1 leading-none" style={{ fontSize: '8px' }}>
                                    {displayDuration} dk ({timeRange})
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showTooltip && createPortal(
                <div style={{
                    position: 'fixed',
                    top: tooltipPos.top,
                    left: tooltipPos.left,
                    transform: 'translate(10px, -50%)',
                    zIndex: 99999,
                    width: 'max-content',
                    minWidth: '200px',
                    backgroundColor: '#111827',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                    opacity: 1,
                    margin: 0,
                    pointerEvents: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    color: '#e2e8f0',
                    fontSize: '12px',
                    fontWeight: 500
                }}>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Belge No:</span>
                        <span className="tooltip-value">{isMasked ? '-' : (reservation.reservationNumber || reservation.documentNumber || '-')}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Tedarikçi:</span>
                        <span className="tooltip-value">{isMasked ? 'REZERVASYONU OLAN NAKLİYECİ' : (reservation.transporterShortName || reservation.transporterName || '-')}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Plaka:</span>
                        <span className="tooltip-value">{isMasked ? 'GİZLİ' : (reservation.plateNumber || '-')}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Sürücü:</span>
                        <span className="tooltip-value">{isMasked ? 'GİZLİ' : (reservation.driverName ? `${reservation.driverName} ${reservation.driverPhone || ''}` : '-')}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Durum:</span>
                        <span className="tooltip-value" style={{
                            color: reservation.statusConfig?.color || statusColor || '#3b82f6',
                            fontWeight: 'bold'
                        }}>
                            {isMasked ? 'GİZLİ' : (reservation.statusConfig?.label || statusLabel || reservation.status || '-')}
                        </span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Saatler:</span>
                        <span className="tooltip-value">{timeRange}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Nakliye Türü:</span>
                        <span className="tooltip-value">{isMasked ? '-' : (reservation.transportType || '-')}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Araç Tipi:</span>
                        <span className="tooltip-value">{isMasked ? '-' : (reservation.vehicleTypeName || reservation.metrics?.vehicleTypeCode || '-')}</span>
                    </div>
                    {!isMasked && reservation.metrics && (
                        <>
                            <div className="tooltip-row">
                                <span className="tooltip-label">Hacim:</span>
                                <span className="tooltip-value">{reservation.metrics.volume} m³</span>
                            </div>
                            <div className="tooltip-row">
                                <span className="tooltip-label">Ağırlık:</span>
                                <span className="tooltip-value">{reservation.metrics.weight} kg</span>
                            </div>
                        </>
                    )}
                    <div className="tooltip-row">
                        <span className="tooltip-label">Süre:</span>
                        <span className="tooltip-value">{displayDuration} dk</span>
                    </div>
                    {reservation.note && !isMasked && (
                        <div className="tooltip-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <span className="tooltip-label" style={{ color: '#fbbf24', marginBottom: '4px' }}>💬 Not:</span>
                            <span className="tooltip-value" style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic', lineHeight: '1.4' }}>{reservation.note}</span>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
};

export default TimelineEvent;
